import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/seo/SEOHead";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Terms of Service | LaunchGuide.io"
        description="Terms governing access to and use of the LaunchGuide platform and website."
        pathname="/terms-of-service"
      />
      <Navbar />
      
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold mb-8 text-center">Terms of Service</h1>
          <p className="text-lg text-muted-foreground mb-8 text-center">
            Last updated: 2 June 2025
          </p>

          <div className="prose prose-lg max-w-none dark:prose-invert space-y-6">
            <h2 className="mb-4">1. Introduction</h2>
            <p className="mb-4">
              These Terms of Service (the "<strong>Terms</strong>") govern your access to and use of the LaunchGuide platform, website (<a href="https://launchguide.io">https://launchguide.io</a>) and related services (collectively, the "<strong>Service</strong>").
            </p>
            <p className="mb-4">
              <strong>PLEASE READ THESE TERMS CAREFULLY. BY ACCESSING OR USING THE SERVICE, YOU CONFIRM THAT YOU HAVE READ, UNDERSTOOD AND AGREE TO BE LEGALLY BOUND BY THEM. IF YOU DO NOT AGREE, YOU MUST NOT ACCESS OR USE THE SERVICE.</strong>
            </p>
            <p className="mb-6">
              If you are entering into these Terms on behalf of a company or other legal entity, you represent and warrant that you have authority to bind that entity. In that case, "you" and "your" refer to the entity.
            </p>

            <h2 className="mb-4">2. Definitions</h2>
            <ul className="mb-6">
              <li><strong>Account</strong>: a user‑specific login that enables access to the Service.</li>
              <li><strong>Content</strong>: any text, images, code, data or other materials submitted, generated, displayed or stored through the Service.</li>
              <li><strong>Subscription</strong>: a paid plan granting access to the Service for a specified period.</li>
              <li><strong>User</strong>: any individual who accesses or uses the Service via an Account.</li>
            </ul>

            <h2 className="mb-4">3. Eligibility</h2>
            <p className="mb-6">
              You must be at least 16 years old and legally capable of entering into contracts. If you are resident in a jurisdiction imposing age restrictions beyond 16, you must comply with the higher age.
            </p>

            <h2 className="mb-4">4. Account Registration & Security</h2>
            <p className="mb-2">You agree to:</p>
            <ol className="mb-4">
              <li>provide accurate, current and complete information;</li>
              <li>keep your credentials confidential;</li>
              <li>promptly update your details if they change;</li>
              <li>notify us immediately at <a href="mailto:hello@launchguide.io">hello@launchguide.io</a> if you suspect unauthorised use.</li>
            </ol>
            <p className="mb-6">
              We may suspend or terminate Accounts that violate these Terms or pose a security risk.
            </p>

            <h2 className="mb-4">5. Subscriptions, Fees & Payment</h2>
            <ol className="mb-6 space-y-3">
              <li><strong>Plans & Pricing</strong>. Details of available plans and fees are displayed in the billing interface and on our website. Prices are expressed in <strong>euros (EUR)</strong> for customers located in the European Economic Area ("EEA") and in <strong>United States dollars (USD)</strong> for customers located in the United States or where invoicing in USD is otherwise offered. Listed prices exclude any value‑added tax (VAT), goods and services tax (GST) or sales tax, which will be added at checkout in accordance with applicable law.</li>
              <li><strong>Billing Cycle</strong>. Paid plans are billed in advance on a recurring monthly or annual basis via <strong>Stripe Payments Europe Ltd.</strong> ("Stripe"). Your Subscription renews automatically unless cancelled.</li>
              <li><strong>Upgrades & Downgrades</strong>. Upgrading is effective immediately; the difference in fees is charged pro‑rata. Downgrades take effect at the next renewal.</li>
              <li><strong>Late Payment</strong>. Failure to pay may lead to suspension. Accounts overdue for 60 days may be terminated and data deleted.</li>
              <li><strong>Right of Withdrawal (Consumers)</strong>. If you are a consumer residing in the EEA, you may withdraw from a new Subscription within 14 days of purchase by emailing <a href="mailto:hello@launchguide.io">hello@launchguide.io</a>. However, once you start using the Service, you acknowledge the digital content has been supplied and waive the withdrawal right.</li>
              <li><strong>Refunds</strong>. Except where required by law, payments are non‑refundable.</li>
            </ol>

            <h2 className="mb-4">6. Free Trial</h2>
            <p className="mb-6">
              We may offer a free trial. At the end of the trial, continued use requires a paid Subscription. We may delete trial data 30 days after expiry.
            </p>

            <h2 className="mb-4">7. Service Description & Beta Features</h2>
            <p className="mb-6">
              LaunchGuide provides AI‑assisted marketing workflows, content creation, analytics and related functionality. We may label certain features as <strong>Beta</strong> or <strong>Preview</strong>; such features are provided "as‑is", may change without notice, and are excluded from any service commitments.
            </p>

            <h2 className="mb-4">8. Acceptable Use Policy</h2>
            <p className="mb-2">You agree not to:</p>
            <ol className="mb-4 space-y-1">
              <li>upload or generate content that is illegal, infringing, harmful, hateful, harassing, or otherwise objectionable;</li>
              <li>violate or encourage violation of any applicable law or regulation;</li>
              <li>reverse‑engineer, decompile, or attempt to extract source code of the Service except as permitted by law;</li>
              <li>overload, interfere with, or disrupt the Service or its networks;</li>
              <li>use automated means (bots, scrapers) without prior written consent;</li>
              <li>misrepresent your identity or affiliation;</li>
              <li>use the Service to build competing products.</li>
            </ol>
            <p className="mb-6">
              We reserve the right to remove content or suspend accounts that breach this policy.
            </p>

            <h2 className="mb-4">9. Intellectual Property</h2>
            <ol className="mb-6 space-y-2">
              <li><strong>Our IP</strong>. We retain all rights in the Service, including software, design, trademarks and documentation.</li>
              <li><strong>Your Content</strong>. You retain ownership of Content you submit. You grant LaunchGuide a worldwide, non‑exclusive licence to host, process, display and use your Content solely to operate and improve the Service.</li>
              <li><strong>Feedback</strong>. If you send suggestions or feedback, you grant us a perpetual, irrevocable, royalty‑free licence to use them without restriction.</li>
            </ol>

            <h2 className="mb-4">10. AI‑Generated Content Disclaimer</h2>
            <p className="mb-6">
              The Service integrates with <strong>OpenAI L.L.C.</strong> to generate AI content. AI outputs are probabilistic and may contain inaccuracies or third‑party material. You are responsible for reviewing outputs before use and ensuring they comply with applicable laws, including intellectual property and advertising regulations.
            </p>

            <h2 className="mb-4">11. Confidentiality</h2>
            <p className="mb-6">
              Each party will protect confidential information disclosed by the other with at least reasonable care and use it only for performing the Agreement. This obligation survives termination for five (5) years.
            </p>

            <h2 className="mb-4">12. Data Protection</h2>
            <p className="mb-2">
              Processing of personal data is governed by our <strong>[Privacy Policy]</strong> and, if applicable, a Data Processing Agreement ("DPA"). LaunchGuide relies on the following sub‑processors:
            </p>
            <ul className="mb-4">
              <li><strong>Netlify, Inc.</strong> – hosting & CDN;</li>
              <li><strong>Supabase Inc.</strong> – managed database & authentication (EU region);</li>
              <li><strong>OpenAI, L.L.C.</strong> – AI generation;</li>
              <li><strong>Stripe Payments Europe Ltd.</strong> – payments.</li>
            </ul>
            <p className="mb-6">
              We implement appropriate technical and organisational measures to protect personal data.
            </p>

            <h2 className="mb-4">13. Third‑Party Services</h2>
            <p className="mb-6">
              The Service may link to or integrate third‑party products. We are not responsible for those services' practices or content. Your use of third‑party services is subject to their separate terms.
            </p>

            <h2 className="mb-4">14. Service Availability & Support</h2>
            <p className="mb-6">
              We aim for 99.5% monthly uptime but do not guarantee uninterrupted availability. Planned maintenance will be scheduled outside business hours (CET) when possible. Support is provided via email during business days 09:00‑18:00 CET.
            </p>

            <h2 className="mb-4">15. Warranties & Disclaimers</h2>
            <p className="mb-6">
              The Service is provided <strong>"as‑is"</strong> and <strong>"as available"</strong>, without warranties of any kind, express or implied. We do not warrant that the Service will be error‑free, secure, or meet your requirements.
            </p>

            <h2 className="mb-4">16. Limitation of Liability</h2>
            <p className="mb-2">To the fullest extent permitted by law:</p>
            <ol className="mb-4 space-y-1">
              <li>We are not liable for indirect, incidental, special, consequential or punitive damages, or for loss of profits, revenue, data or goodwill;</li>
              <li>Our aggregate liability under these Terms will not exceed the total fees paid by you to LaunchGuide in the 12 months preceding the claim.</li>
            </ol>
            <p className="mb-6">
              Nothing limits liability for death or personal injury caused by negligence, fraud, or matters for which liability cannot be excluded under applicable law.
            </p>

            <h2 className="mb-4">17. Indemnification</h2>
            <p className="mb-6">
              You will indemnify and hold LaunchGuide harmless from any claims, damages, liabilities and expenses arising from your Content, your use of the Service in violation of these Terms, or your infringement of third‑party rights.
            </p>

            <h2 className="mb-4">18. Term & Termination</h2>
            <p className="mb-4">
              These Terms commence when you first use the Service and continue until terminated. You may terminate at any time via your account settings; termination is effective at the end of the current billing period. We may terminate or suspend the Service or your Account with 30 days' notice, or immediately in case of breach or legal requirement.
            </p>
            <p className="mb-6">
              Upon termination, your access ceases and we will delete Content within 30 days, except data retained for legal obligations.
            </p>

            <h2 className="mb-4">19. Modifications</h2>
            <p className="mb-6">
              We may update these Terms by posting a revised version and notifying you at least 30 days in advance. Continued use after the effective date constitutes acceptance. If you object, your sole remedy is to terminate before the changes take effect.
            </p>

            <h2 className="mb-4">20. Governing Law & Dispute Resolution</h2>
            <p className="mb-6">
              These Terms are governed by Dutch law. The courts of Amsterdam, Netherlands, have exclusive jurisdiction, except that consumers may bring proceedings in their country of residence under mandatory consumer laws. As a consumer, you may also use the EU Online Dispute Resolution platform: <a href="https://ec.europa.eu/consumers/odr">https://ec.europa.eu/consumers/odr</a>.
            </p>

            <h2 className="mb-4">21. Miscellaneous</h2>
            <ul className="mb-6 space-y-2">
              <li><strong>Entire Agreement</strong>. These Terms constitute the entire agreement between you and LaunchGuide and supersede prior agreements.</li>
              <li><strong>Assignment</strong>. You may not assign your rights or obligations without our prior written consent. We may assign in connection with a merger, acquisition or sale.</li>
              <li><strong>Severability</strong>. If any provision is unenforceable, the remainder remains in effect.</li>
              <li><strong>Force Majeure</strong>. Neither party is liable for failure to perform due to causes beyond reasonable control.</li>
              <li><strong>No Waiver</strong>. Failure to enforce a provision is not a waiver of future enforcement.</li>
            </ul>

            <h2 className="mb-4">22. Contact</h2>
            <p className="mb-8">
              Questions? Email us at <strong><a href="mailto:hello@launchguide.io">hello@launchguide.io</a></strong> or write to our registered address above.
            </p>

            <hr className="my-8" />
            <p className="text-center text-sm text-muted-foreground">
              Version 1.0 – Effective 2 June 2025
            </p>
          </div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TermsOfService; 